import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import { F, th as palette, Over, Btn } from "@/lib/bricks";
import { Page, PageHeader, List, Row, Meta, Empty, TextLink, Loading } from "@/components/calm/Calm";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { startGoogleCalendarOAuth } from "@/lib/googleCalendarConnect";

const KIND_COPY = {
  mls: { title: "MLS", blurb: "Your board feed on your own credentials: PropTx (TRREB), Bridge, another RESO Web API feed or Repliers. Listing sync and lookups pull from it. Publishing stays manual." },
  calendar: { title: "Calendar", blurb: "Showings and closings live on the listing file. Google, Apple, and ICS are optional." },
  esign: { title: "E-sign", blurb: "Native Bricks signing is on. A third-party vendor is not required." },
  docusign: { title: "DocuSign", blurb: "Optional, for offices that already use DocuSign. Native Bricks e-sign stays on." },
  crm: { title: "CRM", blurb: "Contacts on the listing file are native. An outside CRM key is optional." },
};

const CALENDAR_TITLES = { google: "Google Calendar", apple: "Apple Calendar", ics: "ICS feed" };

const isDocuSign = (tool) => tool.kind === "esign" && tool.provider === "docusign";
const isNativeEsign = (tool) => tool.kind === "esign" && tool.provider !== "docusign";

function statusLabel(tool) {
  if (isNativeEsign(tool)) return "On";
  if (isDocuSign(tool) && tool.status !== "connected" && tool.status !== "needs_reconnect" && !tool.platform_configured) return "Not set up";
  if (tool.kind === "mls" && tool.status === "connected") {
    return (tool.sandbox || tool.uses_platform_key) ? "Sandbox" : "Connected (your key)";
  }
  if (tool.status === "connected") return "Connected";
  if (tool.status === "needs_reconnect") return "Reconnect needed";
  return "Not connected";
}

export default function IntegrationsPage() {
  const { resolvedTheme } = useTheme();
  const { session } = useAuth();
  const dark = resolvedTheme === "dark";
  const p = palette(dark);
  const [params, setParams] = useSearchParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [mlsOpen, setMlsOpen] = useState(false);
  const [crmOpen, setCrmOpen] = useState(false);
  const [mlsKey, setMlsKey] = useState("");
  const [mlsProvider, setMlsProvider] = useState("proptx");
  const [mlsDataset, setMlsDataset] = useState("");
  const [mlsBaseUrl, setMlsBaseUrl] = useState("");
  const [mlsOfficeId, setMlsOfficeId] = useState("");
  const [crmForm, setCrmForm] = useState({ provider: "custom", api_key: "", webhook_url: "" });
  const [appleOpen, setAppleOpen] = useState(false);
  const [appleForm, setAppleForm] = useState({ apple_id: "", app_password: "" });

  const BASE = process.env.REACT_APP_BACKEND_URL;
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token}` };

  const load = () => {
    fetch(`${BASE}/api/integrations`, { headers })
      .then((r) => r.json())
      .then((d) => { setData(d && d.tools ? d : { tools: [], google: {} }); setLoading(false); })
      .catch(() => { setData({ tools: [], google: {} }); setLoading(false); });
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  useEffect(() => {
    const d = params.get("docusign");
    if (!d) return;
    if (d === "connected") toast.success("DocuSign connected for this office");
    else if (d === "denied") toast.error("DocuSign access was not granted");
    else if (d === "noaccount") toast.error("That DocuSign login has no eSignature account to send from.");
    else if (d === "expired") toast.error("The DocuSign sign-in took too long. Try Connect again.");
    else toast.error("Could not connect DocuSign");
    setParams({}, { replace: true });
    load();
  }, [params]); // eslint-disable-line

  useEffect(() => {
    const g = params.get("google");
    if (!g) return;
    if (g === "connected") toast.success("Google Calendar connected");
    else if (g === "denied") toast.error("Google Calendar access was not granted");
    else if (g === "noscope") toast.error("Google Calendar permission is required. Native calendar still works.");
    else toast.error("Could not connect Google Calendar");
    setParams({}, { replace: true });
    load();
  }, [params]); // eslint-disable-line

  const parseDetail = async (res) => {
    const body = await res.json().catch(() => ({}));
    const d = body.detail;
    if (typeof d === "string") return d;
    if (d && typeof d.message === "string") return d.message;
    if (d && typeof d === "object") return d.msg || JSON.stringify(d);
    return "Something went wrong.";
  };

  const connectMls = async () => {
    if (!mlsKey.trim()) return toast.error(mlsProvider === "repliers" ? "API key required" : "Access token required");
    if (mlsProvider === "bridge" && !mlsDataset.trim()) return toast.error("Bridge dataset id required");
    if (mlsProvider === "reso" && !mlsBaseUrl.trim()) return toast.error("Feed address required");
    setBusy("mls");
    try {
      const res = await fetch(`${BASE}/api/integrations/mls/connect`, {
        method: "POST", headers, body: JSON.stringify({
          api_key: mlsKey.trim(),
          provider: mlsProvider,
          dataset: mlsDataset.trim() || null,
          base_url: mlsBaseUrl.trim() || null,
          office_mls_id: mlsOfficeId.trim() || null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(typeof body.detail === "string" ? body.detail : "Could not save the MLS connection.");
        return;
      }
      toast.success(body.hint || (body.verified ? "Connected (your key) — pull only; Matrix publish stays human." : "Key saved — pull only; Matrix publish stays human."));
      setMlsOpen(false);
      setMlsKey("");
      load();
    } catch {
      toast.error("Could not save the MLS connection.");
    } finally {
      setBusy("");
    }
  };

  const connectCrm = async () => {
    if (!crmForm.api_key.trim() && !crmForm.webhook_url.trim()) {
      return toast.error("Paste an API key or webhook URL.");
    }
    setBusy("crm");
    try {
      const res = await fetch(`${BASE}/api/integrations/crm/connect`, {
        method: "POST", headers, body: JSON.stringify(crmForm),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(typeof body.detail === "string" ? body.detail : "Could not save CRM credentials.");
        return;
      }
      toast.success(body.hint || "Saved. Native CRM is still the source on the listing file.");
      setCrmOpen(false);
      setCrmForm({ provider: "custom", api_key: "", webhook_url: "" });
      load();
    } catch {
      toast.error("Could not save CRM credentials.");
    } finally {
      setBusy("");
    }
  };

  const syncCrm = async () => {
    setBusy("crm-sync");
    try {
      const res = await fetch(`${BASE}/api/integrations/crm/sync`, { method: "POST", headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(typeof body.detail === "string" ? body.detail : "Sync failed.");
        return;
      }
      const pushed = (body.pushed_updates || 0) + (body.pushed_new || 0);
      const back = body.provider === "follow_up_boss" ? `, ${pushed} sent back to Follow Up Boss${body.push_failed ? ` (${body.push_failed} could not be sent, will retry)` : ""}` : "";
      const d = body.deals || {};
      const dealPushed = (d.deals_pushed_updates || 0) + (d.deals_pushed_new || 0);
      const deals = body.provider === "follow_up_boss"
        ? `. Deals: ${d.deals_imported || 0} imported, ${d.deals_updated || 0} updated, ${dealPushed} sent back${d.deals_push_failed ? ` (${d.deals_push_failed} will retry)` : ""}${d.commissions_recorded ? `, ${d.commissions_recorded} closed deal commission${d.commissions_recorded === 1 ? "" : "s"} recorded` : ""}`
        : "";
      toast.success(`Synced contacts: ${body.imported} imported, ${body.updated} updated${back}${deals}${body.truncated ? " (capped this run, run again for more)" : ""}`);
      load();
    } catch {
      toast.error("Sync failed.");
    } finally {
      setBusy("");
    }
  };

  const syncGoogle = async () => {
    setBusy("gcal-sync");
    try {
      const res = await fetch(`${BASE}/api/integrations/google-calendar/sync`, { method: "POST", headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(typeof body.detail === "string" ? body.detail : "Google Calendar sync failed.");
        load();
        return;
      }
      const removed = body.removed ? `, ${body.removed} removed` : "";
      const failed = body.failed ? ` (${body.failed} could not be sent, will retry)` : "";
      toast.success(`Google Calendar synced: ${body.pushed} sent to Google${removed}, ${body.pulled} brought into Bricks${failed}`);
      load();
    } catch {
      toast.error("Google Calendar sync failed.");
    } finally {
      setBusy("");
    }
  };

  const CAL_PATHS = { google: "google-calendar", apple: "apple-calendar", ics: "ics-feed" };

  const disconnect = async (kind, provider) => {
    const path = kind === "calendar"
      ? `/api/integrations/${CAL_PATHS[provider] || "google-calendar"}/disconnect`
      : kind === "esign" && provider === "docusign"
        ? "/api/integrations/docusign/disconnect"
        : `/api/integrations/${kind}/disconnect`;
    setBusy(`${kind}-${provider}`);
    try {
      const res = await fetch(`${BASE}${path}`, { method: "POST", headers });
      if (!res.ok) {
        toast.error(await parseDetail(res));
        return;
      }
      toast.success("Disconnected");
      load();
    } catch {
      toast.error("Could not disconnect.");
    } finally {
      setBusy("");
    }
  };

  const connectGoogle = async () => {
    setBusy("calendar");
    try {
      const res = await fetch(`${BASE}/api/integrations/google-calendar/start`, { headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(typeof body.detail === "string" ? body.detail : "Google Calendar permission is not granted yet.");
        return;
      }
      const started = await startGoogleCalendarOAuth(body);
      if (started.redirected) return;
      toast.error(started.error || "Google Calendar permission is not granted yet.");
    } catch {
      toast.error("Could not start Google Calendar connect.");
    } finally {
      setBusy("");
    }
  };

  const connectDocuSign = async () => {
    setBusy("esign-docusign");
    try {
      const res = await fetch(`${BASE}/api/integrations/docusign/start`, { headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.url) {
        toast.error(typeof body.detail === "string" ? body.detail : "Could not start DocuSign connect.");
        setBusy("");
        return;
      }
      window.location.href = body.url;
    } catch {
      toast.error("Could not start DocuSign connect.");
      setBusy("");
    }
  };

  const connectApple = async () => {
    if (!appleForm.apple_id.trim() || !appleForm.app_password.trim()) {
      return toast.error("Apple ID and app-specific password are required.");
    }
    setBusy("calendar-apple");
    try {
      const res = await fetch(`${BASE}/api/integrations/apple-calendar/connect`, {
        method: "POST", headers, body: JSON.stringify(appleForm),
      });
      if (!res.ok) {
        toast.error(await parseDetail(res));
        return;
      }
      const body = await res.json().catch(() => ({}));
      toast.success(`Apple Calendar connected${body.calendar_name ? ` · ${body.calendar_name}` : ""}`);
      setAppleOpen(false);
      setAppleForm({ apple_id: "", app_password: "" });
      load();
    } catch {
      toast.error("Could not connect Apple Calendar.");
    } finally {
      setBusy("");
    }
  };

  const feedAction = async (action) => {
    setBusy("calendar-ics");
    try {
      const res = await fetch(`${BASE}/api/integrations/ics-feed/${action}`, { method: "POST", headers });
      if (!res.ok) {
        toast.error(await parseDetail(res));
        return;
      }
      const body = await res.json().catch(() => ({}));
      if (body.feed_url && navigator.clipboard) {
        await navigator.clipboard.writeText(body.feed_url).catch(() => {});
      }
      toast.success(action === "rotate" ? "New link made. The old link stops working." : "Subscribe link copied.");
      load();
    } catch {
      toast.error("Could not make the calendar link.");
    } finally {
      setBusy("");
    }
  };

  const copyFeed = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Subscribe link copied");
    } catch {
      toast.message(url);
    }
  };

  const onConnect = (tool) => {
    if (tool.kind === "mls") return setMlsOpen(true);
    if (tool.kind === "crm") return setCrmOpen(true);
    if (tool.kind === "calendar" && tool.provider === "apple") return setAppleOpen(true);
    if (tool.kind === "calendar" && tool.provider === "ics") return feedAction("connect");
    if (tool.kind === "calendar") return connectGoogle();
    if (isDocuSign(tool)) {
      if (!tool.platform_configured) return toast.message("DocuSign isn't set up on Bricks yet. Native Bricks e-sign works now.");
      if (!tool.can_manage) return toast.message("Ask your office manager to connect DocuSign for the office.");
      return connectDocuSign();
    }
    if (tool.kind === "esign") {
      toast.message("Native Bricks e-sign is already on. A third-party vendor is not configured.");
    }
  };

  if (loading) return <Loading />;

  const tools = data?.tools || [];
  const kind = data?.kind || "";
  const titleFor = (tool) => {
    const base = (isDocuSign(tool) ? KIND_COPY.docusign : KIND_COPY[tool.kind]) || { title: tool.label, blurb: tool.description };
    return tool.kind === "calendar" ? (CALENDAR_TITLES[tool.provider] || tool.label || base.title) : base.title;
  };
  // One main action: fix a broken connection first, otherwise the first tool not yet connected.
  const connectable = (tool) => tool.status !== "connected" && !isNativeEsign(tool)
    && (!isDocuSign(tool) || (tool.can_manage && tool.platform_configured))
    && !(tool.kind === "calendar" && tool.provider === "google" && tool.connect_mode === "blocked")
    && !(tool.kind === "calendar" && tool.provider === "ics");
  const mainTool = tools.find((t) => t.status === "needs_reconnect" && connectable(t)) || tools.find(connectable);

  return (
    <Page testid="integrations-page">
      <PageHeader title="Connected tools"
        subtitle={kind === "agent" || kind === "team_member"
          ? "Your personal connections. Calendar and e-sign work without them."
          : "Office and personal connections. Listing tools work without them."}
        action={mainTool ? (
          <Btn dark={dark} data-testid="integrations-main" onClick={() => onConnect(mainTool)}
            disabled={busy === mainTool.kind || busy === `${mainTool.kind}-${mainTool.provider}`}>
            {mainTool.status === "needs_reconnect" ? "Reconnect" : "Connect"} {titleFor(mainTool)}
          </Btn>
        ) : null} />

      {tools.length === 0 ? <Empty>No connections available for this workspace yet.</Empty> : (
      <List testid="integrations-list">
        {tools.map((tool) => {
          const baseCopy = (isDocuSign(tool) ? KIND_COPY.docusign : KIND_COPY[tool.kind]) || { title: tool.label, blurb: tool.description };
          const copy = tool.kind === "calendar" ? { ...baseCopy, title: CALENDAR_TITLES[tool.provider] || tool.label || baseCopy.title } : baseCopy;
          const busyKey = `${tool.kind}-${tool.provider}`;
          const isFeed = tool.kind === "calendar" && tool.provider === "ics";
          const connected = tool.status === "connected";
          const docusign = isDocuSign(tool);
          const canDisconnect = connected && (!isNativeEsign(tool)) && (!docusign || tool.can_manage);
          const canConnect = !connected && !isNativeEsign(tool) && (!docusign || (tool.can_manage && tool.platform_configured));
          const googleBlocked = tool.kind === "calendar" && tool.provider === "google" && tool.connect_mode === "blocked";
          const canSyncCrm = tool.kind === "crm" && connected && (tool.provider === "follow_up_boss" || tool.provider === "kvcore");
          const canSyncGoogle = tool.kind === "calendar" && tool.provider === "google" && connected && tool.sync_supported;
          const feedLinks = isFeed && connected && tool.feed_url;
          const nextParts = [];
          if (tool.hint) nextParts.push(tool.hint);
          return (
            <Row key={busyKey} testid={`integration-${busyKey}`}
              title={copy.title}
              meta={tool.description || copy.blurb}
              next={(nextParts.length || feedLinks || canSyncCrm || canSyncGoogle || canDisconnect || canConnect) ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center" }}>
                  {nextParts.length ? <Meta parts={nextParts} /> : null}
                  {feedLinks && <>
                    <TextLink onClick={() => copyFeed(tool.feed_url)}>Copy link</TextLink>
                    {tool.webcal_url && <TextLink onClick={() => { window.location.href = tool.webcal_url; }}>Open in Calendar</TextLink>}
                    <TextLink onClick={() => busy !== busyKey && feedAction("rotate")}>New link</TextLink>
                  </>}
                  {canSyncCrm && <TextLink onClick={() => busy !== "crm-sync" && syncCrm()}>{busy === "crm-sync" ? "Syncing…" : "Sync contacts"}</TextLink>}
                  {canSyncGoogle && <TextLink onClick={() => busy !== "gcal-sync" && syncGoogle()}>{busy === "gcal-sync" ? "Syncing…" : "Sync now"}</TextLink>}
                  {canDisconnect && <TextLink onClick={() => !(busy === busyKey || busy === tool.kind) && disconnect(tool.kind, tool.provider)}>{isFeed ? "Turn off" : "Disconnect"}</TextLink>}
                  {canConnect && googleBlocked && <TextLink onClick={() => busy !== tool.kind && onConnect(tool)}>Connect</TextLink>}
                  {canConnect && !googleBlocked && (
                    <Btn variant="ghost" dark={dark} onClick={() => onConnect(tool)} disabled={busy === tool.kind || busy === busyKey}>
                      {tool.status === "needs_reconnect" ? "Reconnect" : isFeed ? "Get link" : "Connect"}
                    </Btn>
                  )}
                </div>
              ) : null}
              status={statusLabel(tool)} attention={tool.status === "needs_reconnect"} />
          );
        })}
      </List>
      )}

      <div style={{ fontSize: 12, color: p.ink4, lineHeight: 1.5 }}>
        Keys are stored on the server and never shown in full. Calendar, e-sign and contacts on a listing file never wait on these connections.
      </div>

      <Dialog open={mlsOpen} onOpenChange={setMlsOpen}>
        <DialogContent style={{ maxWidth: 440, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Connect MLS</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              Bring your own board credentials. Bricks pulls listings and sold data from your feed over the RESO Web API (PropTx for TRREB, Bridge, or another RESO feed) or Repliers. No scraping. Pull only. Publishing to Matrix or Paragon stays manual.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 20 }}>
            <div>
              <Over color={p.ink3}>Feed</Over>
              <select
                value={mlsProvider}
                onChange={(e) => setMlsProvider(e.target.value)}
                style={{ marginTop: 8, width: "100%", padding: "10px 12px", border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 16, fontFamily: F.display, color: p.ink, background: "transparent", outline: "none", boxSizing: "border-box" }}
              >
                <option value="proptx">PropTx (TRREB) RESO Web API</option>
                <option value="bridge">Bridge Interactive</option>
                <option value="reso">Other RESO Web API</option>
                <option value="repliers">Repliers</option>
              </select>
            </div>
            {mlsProvider === "bridge" && (
              <div>
                <Over color={p.ink3}>Bridge dataset id</Over>
                <input value={mlsDataset} onChange={(e) => setMlsDataset(e.target.value)} placeholder="e.g. itso" autoComplete="off" style={{ marginTop: 8, width: "100%", padding: "10px 12px", border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 16, fontFamily: F.mono, color: p.ink, background: "transparent", outline: "none", boxSizing: "border-box" }} />
              </div>
            )}
            {mlsProvider === "reso" && (
              <div>
                <Over color={p.ink3}>RESO Web API address</Over>
                <input value={mlsBaseUrl} onChange={(e) => setMlsBaseUrl(e.target.value)} placeholder="https://api.yourfeed.com/odata" autoComplete="off" style={{ marginTop: 8, width: "100%", padding: "10px 12px", border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 16, fontFamily: F.mono, color: p.ink, background: "transparent", outline: "none", boxSizing: "border-box" }} />
              </div>
            )}
            <div>
              <Over color={p.ink3}>{mlsProvider === "repliers" ? "Repliers API key" : "Access token"}</Over>
              <input
                type="password"
                value={mlsKey}
                onChange={(e) => setMlsKey(e.target.value)}
                placeholder={mlsProvider === "repliers" ? "Repliers API key" : "Bearer access token from your feed"}
                autoComplete="off"
                style={{ marginTop: 8, width: "100%", padding: "10px 12px", border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 16, fontFamily: F.mono, color: p.ink, background: "transparent", outline: "none", boxSizing: "border-box" }}
              />
            </div>
            {mlsProvider !== "repliers" && (
              <div>
                <Over color={p.ink3}>Office MLS ID (for listing sync)</Over>
                <input value={mlsOfficeId} onChange={(e) => setMlsOfficeId(e.target.value)} placeholder="Your board office ID" autoComplete="off" style={{ marginTop: 8, width: "100%", padding: "10px 12px", border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 16, fontFamily: F.mono, color: p.ink, background: "transparent", outline: "none", boxSizing: "border-box" }} />
              </div>
            )}
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setMlsOpen(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={connectMls} disabled={busy === "mls"}>{busy === "mls" ? "Checking…" : "Connect"}</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={appleOpen} onOpenChange={setAppleOpen}>
        <DialogContent style={{ maxWidth: 440, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Connect Apple Calendar</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              New showings and closings you add on a listing file go to your iCloud calendar. Make an app-specific password at account.apple.com under Sign-In and Security. Your Apple ID password will not work here.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 20 }}>
            <div>
              <Over color={p.ink3}>Apple ID</Over>
              <input
                type="email"
                value={appleForm.apple_id}
                onChange={(e) => setAppleForm({ ...appleForm, apple_id: e.target.value })}
                placeholder="you@icloud.com"
                autoComplete="username"
                style={{ marginTop: 8, width: "100%", padding: "10px 12px", border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 16, fontFamily: F.display, color: p.ink, background: "transparent", outline: "none", boxSizing: "border-box" }}
              />
            </div>
            <div>
              <Over color={p.ink3}>App-specific password</Over>
              <input
                type="password"
                value={appleForm.app_password}
                onChange={(e) => setAppleForm({ ...appleForm, app_password: e.target.value })}
                placeholder="xxxx-xxxx-xxxx-xxxx"
                autoComplete="off"
                style={{ marginTop: 8, width: "100%", padding: "10px 12px", border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 16, fontFamily: F.mono, color: p.ink, background: "transparent", outline: "none", boxSizing: "border-box" }}
              />
            </div>
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setAppleOpen(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={connectApple} disabled={busy === "calendar-apple"}>
              {busy === "calendar-apple" ? "Checking…" : "Connect"}
            </Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={crmOpen} onOpenChange={setCrmOpen}>
        <DialogContent style={{ maxWidth: 440, borderRadius: 2, background: p.bg, border: `1px solid ${p.hair}`, padding: 'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: F.display, fontSize: 18, fontWeight: 500, color: p.ink }}>Connect CRM</DialogTitle>
            <DialogDescription style={{ fontFamily: F.display, fontSize: 13, color: p.ink3, marginTop: 4 }}>
              Native CRM stays the source on the listing file. Follow Up Boss and kvCORE keys are verified before saving. Follow Up Boss syncs both ways: its people come in, and Bricks edits and new contacts go back to it. kvCORE Sync pulls its contacts in. For kvCORE, paste an API token generated in kvCORE.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 20 }}>
            <div>
              <Over color={p.ink3}>Provider</Over>
              <select
                value={crmForm.provider}
                onChange={(e) => setCrmForm({ ...crmForm, provider: e.target.value })}
                style={{ marginTop: 8, width: "100%", padding: "10px 12px", border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.display, color: p.ink, background: p.bg, outline: "none" }}
              >
                <option value="custom">Custom / other</option>
                <option value="follow_up_boss">Follow Up Boss (API key, two-way contact and deal sync)</option>
                <option value="kvcore">kvCORE (API token, contact sync)</option>
                <option value="lone_wolf">Lone Wolf (key saved, no sync yet)</option>
              </select>
            </div>
            <div>
              <Over color={p.ink3}>API key</Over>
              <input
                type="password"
                value={crmForm.api_key}
                onChange={(e) => setCrmForm({ ...crmForm, api_key: e.target.value })}
                placeholder="Optional if you paste a webhook"
                autoComplete="off"
                style={{ marginTop: 8, width: "100%", padding: "10px 12px", border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.mono, color: p.ink, background: "transparent", outline: "none", boxSizing: "border-box" }}
              />
            </div>
            <div>
              <Over color={p.ink3}>Webhook URL</Over>
              <input
                type="url"
                value={crmForm.webhook_url}
                onChange={(e) => setCrmForm({ ...crmForm, webhook_url: e.target.value })}
                placeholder="https://…"
                style={{ marginTop: 8, width: "100%", padding: "10px 12px", border: `1px solid ${p.hairS}`, borderRadius: 2, fontSize: 14, fontFamily: F.mono, color: p.ink, background: "transparent", outline: "none", boxSizing: "border-box" }}
              />
            </div>
          </div>
          <DialogFooter style={{ marginTop: 24, gap: 10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setCrmOpen(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={connectCrm} disabled={busy === "crm"}>Save</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
